"use client";
import { Button } from "@commerce-frontend/ui/base/Button/Button";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import { useOutsideClick } from "@commerce-frontend/ui/hooks/outside-click";
import { cn } from "@commerce-frontend/ui/utils/classNames";
import type { Locale } from "@commerce-shared/site-config";
import { Command } from "cmdk";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import React, {
	type FormEvent,
	memo,
	useCallback,
	useEffect,
	useRef,
} from "react";
import { useStaticUrl } from "~/hooks/static-url";
import { QuickSearch } from "../QuickSearch/QuickSearch";
import type { SearchResultObject } from "../SearchBarGroup/SearchBarGroup";

export type SearchBarProps = {
	locale: Locale;
	className?: string;
	value: string;
	showQuickSearch: boolean;
	recentSearchTerms: string[];
	searchResult: SearchResultObject;
	loadingSearchResults: boolean;
	onInputValueChange: (value: string) => void;
	onSelectLink: (
		value: string,
		shouldAddToRecentSearchTerms: boolean,
		shouldCloseSearch?: boolean,
	) => void;
	onSelectProductLink: () => void;
	onClickDeleteLink: (value: string) => void;
	onClickSubmitLink: (value: string) => void;
	onClickCloseButton: () => void;
	onFocusInput: () => void;
	onBlurInput: () => void;
};

export const SearchBar = memo(
	({
		locale,
		className,
		value,
		showQuickSearch,
		recentSearchTerms,
		searchResult,
		loadingSearchResults,
		onInputValueChange,
		onSelectLink,
		onSelectProductLink,
		onClickDeleteLink,
		onClickSubmitLink,
		onClickCloseButton,
		onFocusInput,
		onBlurInput,
	}: SearchBarProps) => {
		const t = useTranslations("SearchBar");

		const commandRef = useRef<HTMLDivElement | null>(null);

		const handleValueChange = useCallback(
			(search: string) => {
				onInputValueChange(search);
			},
			[onInputValueChange],
		);

		// Used to close the quick search when clicking outside of it
		const handleOutsideClick = useCallback(
			(e: MouseEvent | TouchEvent) => {
				const target = e.target as HTMLElement;
				if (target.hasAttribute("cmdk-input")) {
					return;
				}
				onBlurInput();
			},
			[onBlurInput],
		);
		useOutsideClick(commandRef, handleOutsideClick);

		const router = useRouter();
		const searchUrl = useStaticUrl("search");

		// Pressing '/' if not already in a form element will focus the search input
		const handleGlobalKeyDown = useCallback(
			(event: KeyboardEvent) => {
				switch (event.key) {
					case "/":
						if (
							event.target instanceof HTMLInputElement ||
							event.target instanceof HTMLSelectElement ||
							event.target instanceof HTMLTextAreaElement
						) {
							return;
						}
						event.preventDefault();
						document.querySelector<HTMLInputElement>("input[name=q]")?.focus();
						break;
					case "Escape":
						if (
							event.target instanceof HTMLInputElement &&
							event.target.hasAttribute("cmdk-input")
						) {
							const elementsWithAttributes = document.querySelectorAll(
								'[cmdk-item][data-selected="true"]',
							);
							if (elementsWithAttributes.length > 0) {
								elementsWithAttributes[0].removeAttribute("data-selected");
							} else {
								document
									.querySelector<HTMLInputElement>("input[name=q]")
									?.focus();
								onBlurInput();
							}
						}
						break;
					case "Tab":
						if (
							event.target instanceof HTMLInputElement &&
							event.target.hasAttribute("cmdk-input")
						) {
							onBlurInput();
						}
						break;
				}
			},
			[onBlurInput],
		);
		useKeyDown(handleGlobalKeyDown);

		const handleSubmitSearch = useCallback(
			(event: FormEvent<HTMLFormElement>) => {
				event.preventDefault();

				if (value.length < 3) {
					return;
				}

				onClickSubmitLink(value);

				router.push(`${searchUrl}?q=${value}`);

				document
					.querySelector<HTMLAnchorElement>(
						'[aria-label="' + t("accessibilityName") + '"] a',
					)
					?.focus();
			},
			[onClickSubmitLink, router, searchUrl, t, value],
		);

		const handleInputKeyDown = useCallback(
			(event: React.KeyboardEvent<HTMLInputElement>) => {
				if (event.key === "Enter") {
					const elementsWithAttributes = document.querySelectorAll(
						'[cmdk-item][data-selected="true"]',
					);
					if (elementsWithAttributes.length === 0) {
						// Allow default form submit if there are no suggestions
						event.preventDefault();
						handleSubmitSearch({
							preventDefault: () => {},
							target: event.target,
						} as unknown as FormEvent<HTMLFormElement>);
					}
				}
			},
			[handleSubmitSearch],
		);

		return (
			<form
				id="submit-form"
				role="search"
				action={searchUrl} // if js is disabled, the form will still work
				onSubmit={handleSubmitSearch}
				className={cn("relative w-full", className)}
				aria-label="Quick search"
			>
				<Command shouldFilter={false} loop={true}>
					<Command.Input
						alt={t("accessibilityName")}
						name="q"
						autoComplete="off"
						value={value}
						onValueChange={handleValueChange}
						placeholder={t("placeholder")}
						required
						className={cn(
							"focus-visible:outline-0 focus-visible:ring-0 focus-visible:ring-offset-0",
							// shape
							"w-full border border-border bg-background px-4 text-main",
							// font size
							"text-sm leading-6 sm:text-base",
							// reset
							"appearance-none",
							// placeholder
							"placeholder:text-main/50",
							// size
							"py-[0.6875rem]",
							value?.length > 0 && "pr-[4.5rem]",
							showQuickSearch ? "rounded-b-none rounded-t" : "rounded-full",
						)}
						onFocus={onFocusInput}
						onKeyDown={handleInputKeyDown}
					/>
					<Button
						id="search-button"
						className={cn(
							"absolute right-0 top-0 border-transparent bg-transparent text-foreground",
							showQuickSearch ? "rounded" : "rounded-full",
						)}
						variant="outlined"
						icon={<Icon icon="search" />}
						type="submit"
						title={t("search")}
						tabIndex={-1}
						aria-label={t("accessibilitySearchButton")}
					/>
					{value && value.length > 0 && (
						<button
							type="button"
							onClick={onClickCloseButton}
							className="absolute right-12 top-6 flex -translate-y-1/2"
							tabIndex={-1}
							aria-label={t("accessibilityClearButton")}
						>
							<Icon icon="x-circle" className="bg-gray-300" />
						</button>
					)}
					<Command.List
						ref={commandRef}
						className={cn(
							"absolute z-[100] w-full rounded-b bg-background shadow-object",
							showQuickSearch ? "block" : "hidden", // We explicitly hide/show the list instead of unmounting it to avoid issues with a11y
						)}
					>
						{showQuickSearch && (
							<QuickSearch
								locale={locale}
								value={value}
								searchResult={searchResult}
								loadingSearchResults={loadingSearchResults}
								recentSearchTerms={recentSearchTerms}
								onSelectLink={onSelectLink}
								onSelectProductLink={onSelectProductLink}
								onClickDeleteLink={onClickDeleteLink}
							/>
						)}
					</Command.List>
				</Command>
			</form>
		);
	},
);

export const useKeyDown = (handler: (event: KeyboardEvent) => void) =>
	useEffect(() => {
		if (typeof window !== "undefined") {
			window.addEventListener("keydown", handler);
			return () => window.removeEventListener("keydown", handler);
		}
	}, [handler]);

SearchBar.displayName = "SearchBar";
